import React, {
  createContext,
  useContext,
  ReactNode,
  useRef,
  useEffect,
} from "react";
import { fileSystemService } from "../services/fileSystem";
import { FileItem } from "../types/files";
import { useGlobalContext } from "./GlobalContext";
import { Mode } from "types/modes";
import { isSameFlatFileTree, readFileTree } from "../utils/fileTree";

interface FileTreeContextType {

  refreshFileTree: () => Promise<void>;
  getFileByPath: (path: string) => FileItem | undefined;

  // Core file operations
  getFileContent: (path: string) => Promise<string>;
  saveFileContent: (path: string, content: string) => Promise<void>;
}

const FileTreeContext = createContext<FileTreeContextType | undefined>(
  undefined
);

export const FileTreeProvider = ({ children }: { children: ReactNode }) => {
  const {
    mode,
    projectRootPath,
    getCurrentFileTree,
    setFileTree,
    currentDirectory
  } = useGlobalContext();

  const lastFileTreeRef = useRef<FileItem[]>([]);

  const refreshFileTree = async () => {
    if (!projectRootPath) return;
    
    if (mode === Mode.Join || mode === Mode.Host) {
      // SignalR refresh
      return;
    }

    try {
      const refreshed = await readFileTree(projectRootPath);
      const sorted = sortFileTree(refreshed);
      
      if (!isSameFlatFileTree(lastFileTreeRef.current, sorted)) {
        setFileTree(sorted);
        lastFileTreeRef.current = sorted;
      }
    } catch (err) {
      console.error("Failed to refresh file tree:", err);
    }
  };

  useEffect(() => {
    // currentDirectory varsa ve projectRootPath varsa refresh et
    if (currentDirectory && projectRootPath) {
      refreshFileTree();
    }
  }, [currentDirectory]); // currentDirectory dependency'si

  const getFileByPath = (path: string): FileItem | undefined => {
    const currentTree = getCurrentFileTree();
    const search = (items: FileItem[]): FileItem | undefined => {
      for (const item of items) {
        if (item.path === path) return item;
        if (item.isDirectory && (item as any).children) {
          const result = search((item as any).children);
          if (result) return result;
        }
      }
      return undefined;
    };
    return search(currentTree);
  };

  const getFileContent = async (path: string): Promise<string> => {
    if (mode === Mode.Join || mode === Mode.Host) {
      // TODO: fetch from backend/virtual file system
      return "";
    }
    return await fileSystemService.readFile(path);
  };

  const saveFileContent = async (path: string, content: string) => {
    if (mode === Mode.Join || mode === Mode.Host) {
      // TODO: send to backend for virtual tree write
      return;
    }
    try {
      await fileSystemService.writeFile(path, content);
    } catch (err) {
      console.error("Error saving file:", err);
    }
  };

  return (
    <FileTreeContext.Provider
      value={{
        refreshFileTree,
        getFileByPath,
        getFileContent,
        saveFileContent,
      }}
    >
      {children}
    </FileTreeContext.Provider>
  );
};

export const useFileTree = (): FileTreeContextType => {
  const context = useContext(FileTreeContext);
  if (!context) {
    throw new Error("useFileTree must be used within a FileTreeProvider");
  }
  return context;
};

function sortFileTree(items: FileItem[]): FileItem[] {
  const sorted = [...items].sort((a, b) => {
    if (a.isDirectory && !b.isDirectory) return -1;
    if (!a.isDirectory && b.isDirectory) return 1;
    return a.name.localeCompare(b.name);
  });
  return sorted;
}
